import { forwardRef, type HTMLAttributes, type ElementType } from 'react';
import { cn } from '@/utils/cn';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Controls the drop shadow depth applied to the card.
 *
 * Maps directly to the Primer shadow tokens:
 * - `"ground"` → `--shadow-top-light-ground-floor` (8% opacity — subtle)
 * - `"first"`  → `--shadow-top-light-first-floor`  (12% opacity — medium)
 * - `"second"` → `--shadow-top-light-second-floor` (20% opacity — strong)
 */
export type CardShadow = 'ground' | 'first' | 'second';

/**
 * Card component props.
 *
 * `className` is intentionally omitted. Card is a sealed design system
 * component — its visual chrome (border, radius, background, overflow) is
 * fixed by design. Layout and spacing belong on the parent container or on
 * child elements inside the card, not on the card root itself. This prevents
 * accidental design drift across the app.
 *
 * If you need a new visual variant, add a prop here rather than reaching for
 * a one-off className override.
 */
export interface CardProps extends Omit<HTMLAttributes<HTMLElement>, 'className'> {
  /**
   * Shadow depth. Omit for a flat card with no drop shadow.
   *
   * @see CardShadow
   */
  shadow?: CardShadow;
  /**
   * When true, renders the card background at 56% opacity so the gradient
   * behind it shows through. Used on pre-auth screens per the Figma design.
   */
  translucent?: boolean;
  /**
   * The HTML element or React component to render as the card root.
   * Defaults to `"div"`. Use `"article"`, `"section"`, `"li"`, etc. as
   * needed to satisfy document semantics.
   *
   * @default "div"
   */
  as?: ElementType;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const shadowMap: Record<CardShadow, string> = {
  ground: 'shadow-(--shadow-top-light-ground-floor)',
  first: 'shadow-(--shadow-top-light-first-floor)',
  second: 'shadow-(--shadow-top-light-second-floor)',
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Primer Card component.
 *
 * A minimal structural shell that applies the standard card chrome — border,
 * radius, surface background, and overflow clipping — without imposing any
 * internal layout or spacing.
 *
 * Consumers are entirely responsible for the card's interior. This keeps the
 * base card compatible with full-bleed images, horizontal/vertical separators,
 * coloured header bands, and any other layout pattern that would be broken by
 * opinionated padding or flex rules.
 *
 * ### Sealed component
 * `className` is not exposed. Card's visual chrome is intentionally fixed.
 * Layout (width, margin, grid placement) belongs on the **parent container**.
 * Content spacing belongs on **child elements** inside the card.
 * If a new visual state is needed, add a prop — don't override via className.
 *
 * ### Always applied
 * - `border border-default` — 1px solid border using the default border token
 * - `rounded-medium` — radius-medium corner rounding
 * - `bg-surface` — white surface background
 * - `overflow-hidden` — clips children to the card's rounded corners
 *
 * ### Shadow
 * Pass `shadow` to apply a drop shadow. The three levels map directly to the
 * Primer shadow tokens:
 * - `"ground"` — subtle (8% opacity)
 * - `"first"`  — medium (12% opacity)
 * - `"second"` — strong (20% opacity)
 *
 * ### Polymorphism
 * Use the `as` prop to change the root element for semantic correctness:
 * `<Card as="article">`, `<Card as="li">`, etc.
 *
 * @example
 * // Basic flat card — width controlled by the parent container
 * <div className="w-80">
 *   <Card>
 *     <div className="p-4">
 *       <Typography variant="body" size="medium">Hello world</Typography>
 *     </div>
 *   </Card>
 * </div>
 *
 * @example
 * // Card with shadow
 * <Card shadow="first">
 *   <div className="p-4">
 *     <Typography variant="body" size="medium">Elevated card</Typography>
 *   </div>
 * </Card>
 *
 * @example
 * // Full-bleed image + padded content — overflow-hidden clips the image to radius
 * <Card as="article">
 *   <img src="/product.jpg" alt="Product" className="w-full" />
 *   <div className="p-4">
 *     <Typography variant="header" size="h4">Product name</Typography>
 *   </div>
 * </Card>
 *
 * @see src/primer/README.md
 */
export const Card = forwardRef<HTMLElement, CardProps>(function Card(
  { shadow, translucent, as: Component = 'div', children, ...props },
  ref
) {
  return (
    <Component
      ref={ref}
      className={cn(
        // ── Structure ──────────────────────────────────────────────────────
        'rounded-medium',
        'border border-default',
        translucent ? 'bg-surface-translucent' : 'bg-surface',
        // Flex column so children (e.g. CampaignCard content pane) can use
        // flex-1 / mt-auto to push elements to the bottom of the card.
        'flex flex-col',
        // Clip children (e.g. full-bleed images) to the rounded corners
        'overflow-hidden',
        // ── Shadow (optional) ──────────────────────────────────────────────
        shadow && shadowMap[shadow]
      )}
      {...props}
    >
      {children}
    </Component>
  );
});

export default Card;
